import React, { useEffect, useRef, useState } from "react";

const MAX_MESSAGES = 10;
const SESSION_MINUTES = 10;
const OPENING = "What is your name?";

export default function GuestChat() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: OPENING },
  ]);
  const [input, setInput] = useState("");
  const [userCount, setUserCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expired, setExpired] = useState(false);
  const [timeLeft, setTimeLeft] = useState(SESSION_MINUTES * 60);
  const [sessionId] = useState(() => `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(interval); setExpired(true); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (!expired && userCount < MAX_MESSAGES) inputRef.current?.focus();
  }, [messages, expired, userCount]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading || userCount >= MAX_MESSAGES || expired) return;
    const newMessages = [...messages, { role: "user", content: text }];
    setMessages(newMessages);
    setInput("");
    setUserCount(c => c + 1);
    setLoading(true);
    try {
      const res = await fetch("/api/guest-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: newMessages, sessionId }),
      });
      const data = await res.json();
      if (!res.ok || !data.reply) {
        setMessages(prev => [...prev, { role: "assistant", content: `Error: ${data.error || "unknown"}` }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: data.reply }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Signal lost. Try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = e => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const remaining = MAX_MESSAGES - userCount;
  const mins = Math.floor(timeLeft / 60);
  const secs = String(timeLeft % 60).padStart(2, "0");
  const isLocked = expired || userCount >= MAX_MESSAGES;
  const timerUrgent = timeLeft <= 60;

  return (
    <div className="gc">
      <div className="gc__header">
        <div className="gc__header-left">
          <img src="/img/veldran.gif" alt="Veldran" className="gc__avatar" />
          <span className="gc__name">VELDRAN</span>
        </div>
        {!isLocked && (
          <span className={`gc__timer${timerUrgent ? " gc__timer--urgent" : ""}`}>
            {mins}:{secs}
          </span>
        )}
      </div>

      <div className="gc__feed">
        {messages.map((m, i) => (
          <div key={i} className={`gc__row gc__row--${m.role}`}>
            {m.role === "assistant"
              ? <p className="gc__text gc__text--bot">{m.content}</p>
              : <p className="gc__text gc__text--user">{m.content}</p>
            }
          </div>
        ))}

        {loading && (
          <div className="gc__row gc__row--assistant">
            <div className="gc__typing">
              <span /><span /><span />
            </div>
          </div>
        )}

        {isLocked && (
          <div className="gc__lock">
            <span className="gc__lock-label">
              {expired ? "Session expired" : "Limit reached"}
            </span>
            <a
              href="https://t.me/bytegen_dev_bot"
              target="_blank"
              rel="noopener noreferrer"
              className="gc__tg-btn"
            >
              Continue on Telegram
            </a>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {!isLocked && (
        <div className="gc__bottom">
          <div className="gc__input-row">
            <input
              ref={inputRef}
              className="gc__input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Message..."
              maxLength={500}
              disabled={loading}
            />
            <button
              className="gc__send"
              onClick={send}
              disabled={loading || !input.trim()}
              aria-label="Send"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M13 7L1 1.5 3.5 7 1 12.5 13 7Z" fill="currentColor" />
              </svg>
            </button>
          </div>
          <div className="gc__status">
            <div className="gc__pips">
              {Array.from({ length: MAX_MESSAGES }).map((_, i) => (
                <span key={i} className={`gc__pip${i < userCount ? " gc__pip--used" : ""}`} />
              ))}
            </div>
            <span className="gc__remain">{remaining} / {MAX_MESSAGES}</span>
          </div>
        </div>
      )}
    </div>
  );
}
